import { z } from "zod";

// An agent is a record, not a machine (ADR-023 §1): a name, the member it acts for, the
// organization, how it is hosted and which harness drives it. Its status is what the runtime
// last reported; creating one starts nothing, so a new agent is asleep. Its key and its own
// permission list never travel: they stay on the server (`zk_<id>.<secret>`, stored as a sha256).

/** `remote`: we run it on our pool. `connected` is reserved for connecting your own agent, which
 * is designed and deferred (ADR-023 § Deferred). */
export const AGENT_HOSTINGS = ["remote", "connected"] as const;
export const agentHostingSchema = z.enum(AGENT_HOSTINGS);
export type AgentHosting = z.infer<typeof agentHostingSchema>;

/** The member's own Claude, the member's own Codex, or our model billed in AI credits (§7). */
export const AGENT_HARNESSES = ["claude-code", "codex", "zaistev-model"] as const;
export const agentHarnessSchema = z.enum(AGENT_HARNESSES);
export type AgentHarness = z.infer<typeof agentHarnessSchema>;

/** What the runtime last said; `stopped` once a task was ended by hand or the agent idled out. */
export const AGENT_STATUSES = ["asleep", "waking", "running", "idle", "stopped"] as const;
export const agentStatusSchema = z.enum(AGENT_STATUSES);
export type AgentStatus = z.infer<typeof agentStatusSchema>;

export const agentNameSchema = z.string().trim().min(1).max(60);

export const agentLastTaskSchema = z.object({
  /** The instruction as the member gave it, shortened by whoever records it. */
  title: z.string(),
  at: z.string(),
});
export type AgentLastTask = z.infer<typeof agentLastTaskSchema>;

/** What a caller may see about an agent: never its key, never its key's hash. */
export const agentSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  ownerUserId: z.string(),
  name: z.string(),
  hosting: agentHostingSchema,
  harness: agentHarnessSchema,
  status: agentStatusSchema,
  lastTask: agentLastTaskSchema.nullable(),
  createdAt: z.string(),
});
export type Agent = z.infer<typeof agentSchema>;

export const createAgentInputSchema = z.object({ name: agentNameSchema, harness: agentHarnessSchema });
export type CreateAgentInput = z.infer<typeof createAgentInputSchema>;

export const agentIdInputSchema = z.object({ id: z.string().min(1).max(64) });
export type AgentIdInput = z.infer<typeof agentIdInputSchema>;

export const renameAgentInputSchema = z.object({ id: z.string().min(1).max(64), name: agentNameSchema });
export type RenameAgentInput = z.infer<typeof renameAgentInputSchema>;

export const emptyAgentInputSchema = z.object({});

export const listAgentsOutputSchema = z.object({ agents: z.array(agentSchema) });
export type ListAgentsOutput = z.infer<typeof listAgentsOutputSchema>;

/** Which harnesses a new agent may run on: our model only once its key is set (D550). */
export const agentOffersSchema = z.object({ harnesses: z.array(agentHarnessSchema) });
export type AgentOffers = z.infer<typeof agentOffersSchema>;

export const okSchema = z.object({ ok: z.literal(true) });
export type Ok = z.infer<typeof okSchema>;

/** A turn token's payload (seam 1): who the turn acts for, and until when (unix seconds). */
export const turnClaimsSchema = z.object({
  agentId: z.string().min(1),
  organizationId: z.string().min(1),
  ownerUserId: z.string().min(1),
  turnId: z.string().min(1),
  exp: z.number().int().positive(),
});
export type TurnClaims = z.infer<typeof turnClaimsSchema>;

/** Why a turn token was not accepted; never more detail than this travels or is logged. */
export type TurnTokenRefusal = "malformed" | "bad_signature" | "expired";

export type TurnTokenVerdict =
  | { readonly valid: true; readonly claims: TurnClaims }
  | { readonly valid: false; readonly reason: TurnTokenRefusal };

/** Who an agent is, as its own doors answer it (`GET /api/agents/me`): never its key. */
export const agentSelfSchema = z.object({
  id: z.string(),
  name: z.string(),
  organizationId: z.string(),
  ownerUserId: z.string(),
  harness: agentHarnessSchema,
  /** What it may do now: its owner's role cut to its own list. */
  permissions: z.array(z.string()),
});
export type AgentSelf = z.infer<typeof agentSelfSchema>;
